import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import EvidenceSnapshotView from "@/components/EvidenceSnapshotView";
import {
    ArrowLeft, AlertCircle, Mail, Calendar, Shield, User as UserIcon,
    History, FileText, ChevronRight, Github, Globe, ShieldCheck, RefreshCw,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { TIER_COLORS } from "@/lib/tiers";


function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
    } catch {
        return "—";
    }
}

const TABS = [
    { id: "scores", label: "Score History", icon: History },
    { id: "evidence", label: "Current Evidence", icon: FileText },
    { id: "consent", label: "Consent State", icon: Shield },
];

export default function AdminUserDetail() {
    const { userId } = useParams();
    const [user, setUser] = useState(null);
    const [scoresPage, setScoresPage] = useState({ scores: [], page: 1, total: 0, total_pages: 1 });
    const [evidence, setEvidence] = useState([]);
    const [activeTab, setActiveTab] = useState("scores");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const [scoresPageNum, setScoresPageNum] = useState(1);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setErrorStatus(null);
            try {
                const [u, s, e] = await Promise.all([
                    api.get(`/api/admin/users/${userId}`),
                    api.get(`/api/admin/users/${userId}/scores`, { params: { page: scoresPageNum, page_size: 20 } }),
                    api.get(`/api/admin/users/${userId}/evidence`),
                ]);
                if (cancelled) return;
                setUser(u.data);
                setScoresPage(s.data);
                setEvidence(e.data.evidence || []);
            } catch (err) {
                if (cancelled) return;
                setErrorStatus(err.response?.status);
                setError(err.response?.data?.detail || "Couldn't load user data.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, scoresPageNum, reloadKey]);

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh">
                <p className="text-slate-500">Loading user details...</p>
            </div>
        );
    }

    if (error || !user) {
        const isForbidden = errorStatus === 403 || errorStatus === 401;
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh">
                <div className="rounded-xl border border-rose-200 bg-white p-8 max-w-md text-center shadow-sm">
                    <AlertCircle size={32} className="text-rose-500 mx-auto mb-3" />
                    <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                        {isForbidden ? "Access Denied" : "User not found"}
                    </h2>
                    <p className="text-slate-500 text-sm mb-4">
                        {isForbidden ? "You do not have administrative privileges to view this user." : (error || "This user could not be loaded.")}
                    </p>
                    <Link to="/genz-credit-scoring/admin" className="text-blue-700 text-sm font-medium hover:underline">
                        Back to admin console
                    </Link>
                </div>
            </div>
        );
    }

    const consentScopes = user.consent?.consents || {};
    const consentList = Object.entries(consentScopes);
    const oauthList = Object.entries(user.oauth_connections || {});

    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="admin-user-detail">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="max-w-6xl mx-auto"
            >
                <Link
                    to="/genz-credit-scoring/admin"
                    className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-4 transition-colors"
                >
                    <ArrowLeft size={14} /> Back to admin console
                </Link>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
                    <div className="flex items-start gap-4 mb-4">
                        {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="w-16 h-16 rounded-full border border-slate-200 shrink-0" />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                <UserIcon size={28} className="text-slate-500" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                                    {user.name || "Unnamed user"}
                                </h1>
                                {user.role === "admin" && (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">ADMIN</span>
                                )}
                                {user.is_demo && (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">DEMO</span>
                                )}
                            </div>
                            <p className="text-slate-600 text-sm mt-1 flex items-center gap-1.5">
                                <Mail size={13} /> {user.email}
                            </p>
                            <p className="text-slate-500 text-xs mt-1 flex items-center gap-1.5">
                                <Calendar size={12} /> Joined {formatDate(user.created_at)}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
                        <div>
                            <p className="text-slate-500 text-xs">Total Scores</p>
                            <p className="font-mono text-2xl font-bold text-slate-900">{user.total_scores || 0}</p>
                        </div>
                        <div>
                            <p className="text-slate-500 text-xs">Latest Score</p>
                            {user.latest_score ? (
                                <p className="font-mono text-2xl font-bold" style={{ color: TIER_COLORS[user.latest_score.tier] || "#334155" }}>
                                    {user.latest_score.score}
                                </p>
                            ) : (
                                <p className="font-mono text-2xl font-bold text-slate-400">—</p>
                            )}
                        </div>
                        <div>
                            <p className="text-slate-500 text-xs">Latest Tier</p>
                            <p className="text-xl font-semibold text-slate-900">{user.latest_score?.tier || "—"}</p>
                        </div>
                        <div>
                            <p className="text-slate-500 text-xs">Evidence Items</p>
                            <p className="font-mono text-2xl font-bold text-slate-900">{user.total_evidence || 0}</p>
                        </div>
                    </div>

                    {oauthList.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                            <p className="text-slate-500 text-xs mb-2">OAuth Connections</p>
                            <div className="flex flex-wrap gap-2">
                                {oauthList.map(([provider, conn]) => (
                                    <span key={provider} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                                        {provider === "github" ? <Github size={12} /> : <Globe size={12} />}
                                        {provider}
                                        {conn?.username && <span className="text-slate-500">· {conn.username}</span>}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                <div className="flex gap-1 mb-4 border-b border-slate-200">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const active = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                                    active
                                        ? "border-blue-600 text-blue-700"
                                        : "border-transparent text-slate-500 hover:text-slate-700"
                                }`}
                                data-testid={`admin-user-tab-${tab.id}`}
                            >
                                <Icon size={14} /> {tab.label}
                            </button>
                        );
                    })}
                </div>

                {activeTab === "scores" && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading text-lg font-medium text-slate-900">
                                Score History ({scoresPage.total || 0})
                            </h3>
                            <button
                                onClick={() => setReloadKey((value) => value + 1)}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
                            >
                                <RefreshCw size={12} /> Refresh
                            </button>
                        </div>
                        {scoresPage.scores.length === 0 ? (
                            <p className="text-slate-500 text-sm">No scores generated yet.</p>
                        ) : (
                            <>
                                <div className="overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-100">
                                            <tr className="text-left text-xs text-slate-600 uppercase">
                                                <th className="py-2.5 px-3">Date</th>
                                                <th className="py-2.5 px-3">Score</th>
                                                <th className="py-2.5 px-3">Tier</th>
                                                <th className="py-2.5 px-3">Confidence</th>
                                                <th className="py-2.5 px-3">Evidence</th>
                                                <th className="py-2.5 px-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scoresPage.scores.map((s) => (
                                                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                                                    <td className="py-2.5 px-3 text-slate-700">{formatDate(s.created_at)}</td>
                                                    <td className="py-2.5 px-3 font-mono font-bold" style={{ color: TIER_COLORS[s.tier] || "#334155" }}>
                                                        {s.score}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-slate-700">{s.tier}</td>
                                                    <td className="py-2.5 px-3 font-mono text-slate-700">
                                                        {Math.round((s.confidence || 0) * 100)}%
                                                    </td>
                                                    <td className="py-2.5 px-3 text-slate-700">
                                                        {s.evidence_count ?? s.evidence_snapshot?.length ?? 0}
                                                    </td>
                                                    <td className="py-2.5 px-3">
                                                        <Link
                                                            to={`/genz-credit-scoring/admin/scores/${s.id}`}
                                                            className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                                                        >
                                                            View <ChevronRight size={12} />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {scoresPage.total_pages > 1 && (
                                    <div className="flex items-center justify-between mt-4">
                                        <p className="text-xs text-slate-500">
                                            Page {scoresPage.page} of {scoresPage.total_pages}
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setScoresPageNum((p) => Math.max(1, p - 1))}
                                                disabled={scoresPage.page <= 1}
                                                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                Previous
                                            </button>
                                            <button
                                                onClick={() => setScoresPageNum((p) => Math.min(scoresPage.total_pages, p + 1))}
                                                disabled={scoresPage.page >= scoresPage.total_pages}
                                                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}

                {activeTab === "evidence" && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">
                            Current Evidence Collection ({evidence.length})
                        </h3>
                        <p className="text-slate-500 text-sm mb-5">
                            Live evidence on file for this user — what they have <em>right now</em>. Historical scores show what was captured at the time of generation.
                        </p>
                        <EvidenceSnapshotView
                            evidence={evidence}
                            theme="light"
                            emptyMessage="This user has no evidence on file."
                        />
                    </section>
                )}

                {activeTab === "consent" && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">Consent State</h3>
                        <p className="text-slate-500 text-sm mb-5">
                            Current consent grants per scope. Audit trail of changes is available in the main consent log.
                        </p>
                        {consentList.length === 0 ? (
                            <p className="text-slate-500 text-sm italic">No consent record found for this user.</p>
                        ) : (
                            <div className="space-y-2.5">
                                {consentList.map(([scope, scopeData]) => {
                                    const granted = scopeData?.granted;
                                    return (
                                        <div key={scope} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                                            <div className="min-w-0">
                                                <p className="text-slate-900 text-sm font-medium capitalize">{scope.replace(/_/g, " ")}</p>
                                                {scopeData?.timestamp && (
                                                    <p className="text-slate-500 text-xs">Updated {formatDate(scopeData.timestamp)}</p>
                                                )}
                                            </div>
                                            <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 shrink-0 ${
                                                granted ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                                            }`}>
                                                <ShieldCheck size={10} /> {granted ? "Granted" : "Revoked"}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}
            </motion.div>
        </div>
    );
}
